import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Star, MapPin, ArrowRight } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { RestaurantDetailModal } from './RestaurantDetailModal';
import { SponsoredBadge } from './SponsoredBadge';

export function RestaurantListModal({ categoryName, onClose }) {
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  // Mock restaurant data from ratefull folder - shows the flow with dummy data
  const mockRestaurants = [
    {
      id: '1',
      name: 'Bella Napoli',
      cuisine: 'Italian · Neapolitan',
      address: '342 Bleecker Street, New York, NY 10014',
      phone: '+1 (212) 555-0123',
      rating: 4.8,
      reviews: 342,
      priceRange: '$$$',
      hours: '5:00 PM - 10:30 PM',
      images: [
        'https://images.unsplash.com/photo-1758648207365-df458d3e83f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBlbGVnYW50JTIwZGluaW5nfGVufDF8fHx8MTc2NDAwMDI4MXww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1667207394004-acb6aaf4790e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMG1hcmdoZXJpdGElMjBjbG9zZSUyMHVwfGVufDF8fHx8MTc2NDA5ODA5NHww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1675965218658-bd6f516742ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwdGFibGUlMjBzZXR0aW5nfGVufDF8fHx8MTc2NDA2NTE3OHww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1740727665746-cfe80ababc23?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwY29va2luZyUyMGtpdGNoZW58ZW58MXx8fHwxNzYzOTkxMjI2fDA&ixlib=rb-4.1.0&q=80&w=1080',
      ],
      description: 'Experience authentic Neapolitan pizza in the heart of Greenwich Village. Our wood-fired oven, imported from Naples, creates the perfect crispy yet chewy crust. Every ingredient is carefully sourced from Italy to bring you an authentic taste of Southern Italian cuisine.',
      specialties: ['Margherita DOC', 'Marinara', 'Diavola', 'Quattro Formaggi', 'Prosciutto e Rucola'],
      sponsored: true,
      owner: {
        name: 'Giovanni Russo',
        photo: 'https://images.unsplash.com/photo-1759521296047-89338c8e083d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWx8ZW58MXx8fHwxNzYzOTg4NTQwfDA&ixlib=rb-4.1.0&q=80&w=1080',
        bio: 'Born and raised in Naples, Giovanni brings three generations of pizza-making tradition to New York City.',
        story: 'I grew up watching my grandfather knead dough in our family pizzeria in Naples. The smell of wood-fired ovens and San Marzano tomatoes filled my childhood. When I moved to New York, I knew I had to share the authentic flavors of my hometown. Every pizza we make honors the traditions passed down through my family.',
        favoritedish: 'Margherita DOC with buffalo mozzarella',
        social: {
          instagram: 'https://instagram.com/bellanapolinyc',
          facebook: 'https://facebook.com/bellanapolinyc',
          website: 'https://bellanapoli.com',
        },
        interview: [
          {
            question: 'What makes Neapolitan pizza special?',
            answer: 'It\'s all about simplicity and quality. We use only the finest ingredients - San Marzano tomatoes, buffalo mozzarella from Campania, and flour milled specifically for pizza. The dough ferments for at least 24 hours, and we cook each pizza in our wood-fired oven at 900°F for just 90 seconds. This creates the perfect balance of crispy and chewy.',
          },
          {
            question: 'What inspired you to open Bella Napoli?',
            answer: 'When I first arrived in New York, I couldn\'t find authentic Neapolitan pizza. Everything was too thick, too heavy, or used the wrong ingredients. I wanted to create a place where New Yorkers could experience the real thing - the pizza I grew up eating in Naples.',
          },
          {
            question: 'Any advice for aspiring pizzaiolos?',
            answer: 'Respect the craft. Pizza-making is an art that takes years to master. Don\'t rush the fermentation, don\'t skimp on ingredients, and always taste everything. Most importantly, make pizza with love - people can taste it in every bite.',
          },
        ],
      },
    },
    {
      id: '2',
      name: 'L\'Artisan',
      cuisine: 'Modern Italian · Fine Dining',
      address: '789 Madison Avenue, New York, NY 10065',
      phone: '+1 (212) 555-0456',
      rating: 4.9,
      reviews: 521,
      priceRange: '$$$$',
      hours: '6:00 PM - 11:00 PM',
      images: [
        'https://images.unsplash.com/photo-1675965218658-bd6f516742ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwdGFibGUlMjBzZXR0aW5nfGVufDF8fHx8MTc2NDA2NTE3OHww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1758648207365-df458d3e83f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBlbGVnYW50JTIwZGluaW5nfGVufDF8fHx8MTc2NDAwMDI4MXww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1667207394004-acb6aaf4790e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMG1hcmdoZXJpdGElMjBjbG9zZSUyMHVwfGVufDF8fHx8MTc2NDA5ODA5NHww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1740727665746-cfe80ababc23?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwY29va2luZyUyMGtpdGNoZW58ZW58MXx8fHwxNzYzOTkxMjI2fDA&ixlib=rb-4.1.0&q=80&w=1080',
      ],
      description: 'A contemporary take on Italian classics with seasonal ingredients and innovative techniques. Chef Giovanni brings his Michelin-starred experience to create unforgettable dining moments.',
      specialties: ['Truffle Risotto', 'Osso Buco', 'Handmade Tortellini', 'Branzino al Cartoccio'],
    },
    {
      id: '3',
      name: 'Roma Antica',
      cuisine: 'Roman · Traditional',
      address: '156 Mulberry Street, New York, NY 10013',
      phone: '+1 (212) 555-0789',
      rating: 4.7,
      reviews: 298,
      priceRange: '$$',
      hours: '12:00 PM - 10:00 PM',
      images: [
        'https://images.unsplash.com/photo-1740727665746-cfe80ababc23?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGVmJTIwY29va2luZyUyMGtpdGNoZW58ZW58MXx8fHwxNzYzOTkxMjI2fDA&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1667207394004-acb6aaf4790e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMG1hcmdoZXJpdGElMjBjbG9zZSUyMHVwfGVufDF8fHx8MTc2NDA5ODA5NHww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1758648207365-df458d3e83f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBlbGVnYW50JTIwZGluaW5nfGVufDF8fHx8MTc2NDAwMDI4MXww&ixlib=rb-4.1.0&q=80&w=1080',
        'https://images.unsplash.com/photo-1675965218658-bd6f516742ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwdGFibGUlMjBzZXR0aW5nfGVufDF8fHx8MTc2NDA2NTE3OHww&ixlib=rb-4.1.0&q=80&w=1080',
      ],
      description: 'Family-owned trattoria serving traditional Roman recipes passed down through generations. Warm atmosphere and genuine hospitality make every visit special.',
      specialties: ['Cacio e Pepe', 'Carbonara', 'Amatriciana', 'Saltimbocca', 'Tiramisu'],
    },
  ];

  // Use mock data directly to show the flow
  const restaurants = mockRestaurants;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 overflow-y-auto"
          onClick={onClose}
        >
          <div className="min-h-screen px-4 py-8 flex items-start justify-center">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ duration: 0.4 }}
              className="bg-background max-w-5xl w-full shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 z-10 w-10 h-10 border border-border hover:border-foreground bg-background flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="p-12 pb-8 border-b border-border">
                <div className="w-1 h-12 bg-border mb-6"></div>
              <h1 className="serif text-6xl mb-4">{categoryName}</h1>
              <p className="text-sm text-muted-foreground">
                {restaurants.length} restaurants available for reservation
              </p>
              </div>

              {/* Restaurant List */}
              <div className="p-12 space-y-8">
                {restaurants.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-muted-foreground">No restaurants found for this category.</p>
                  </div>
                ) : (
                  restaurants.map((restaurant, index) => (
                    <motion.article
                      key={restaurant.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      onClick={() => setSelectedRestaurant(restaurant)}
                      className="group cursor-pointer pb-8 border-b border-border last:border-0 hover:border-foreground transition-colors"
                    >
                      <div className="grid grid-cols-12 gap-8">
                        {/* Image */}
                        <div className="col-span-4">
                          <div className="aspect-[4/3] overflow-hidden">
                            <ImageWithFallback
                              src={restaurant.images[0]}
                              alt={restaurant.name}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="col-span-8 flex flex-col justify-between">
                          <div>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h2 className="serif text-4xl group-hover:text-muted-foreground transition-colors mb-2">
                                  {restaurant.name}
                                </h2>
                                {restaurant.sponsored && (
                                  <div className="mb-3">
                                    <SponsoredBadge variant="minimal" />
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 bg-stone-100 px-3 py-1.5">
                                <Star className="w-3.5 h-3.5 fill-current" />
                                <span className="text-xs">{restaurant.rating.toFixed(1)}</span>
                              </div>
                            </div>
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
                              {restaurant.cuisine} · {restaurant.priceRange}
                            </p>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                              {restaurant.description}
                            </p>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-6 text-xs text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" />
                                <span>{restaurant.address.split(',')[1] || restaurant.address}</span>
                              </div>
                              <span>·</span>
                              <span>{restaurant.reviews} reviews</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest group-hover:gap-3 transition-all">
                              <span>View & Reserve</span>
                              <ArrowRight className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Restaurant Detail Modal */}
      <AnimatePresence>
        {selectedRestaurant && (
          <RestaurantDetailModal
            restaurant={selectedRestaurant}
            onClose={() => setSelectedRestaurant(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

